// Marketplace — public template directory (audit Phase 3).
//
// Drives the five backend endpoints in ``backend/server.py``:
//   GET    /api/marketplace/templates           → listing (filters: q, type, tag)
//   GET    /api/marketplace/templates/:slug     → detail (with body)
//   POST   /api/marketplace/templates           → submit
//   POST   /api/marketplace/templates/:slug/install
//                                               → bumps download counter,
//                                                 returns body for paste-in
//   DELETE /api/marketplace/templates/:slug     → author or admin
//
// V1 scope: list, search, detail dialog, submit dialog, install (copy
// body to clipboard + show JSON), delete (author/admin). No
// moderation queue — submissions auto-publish; the operator
// community self-polices via the delete endpoint.

import React, { useEffect, useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import ConfirmDialog from './ConfirmDialog';
import { API } from '../App';
import { useT } from '../i18n';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  Store,
  Search,
  Plus,
  Download,
  Trash2,
  Copy,
  Eye,
} from 'lucide-react';
import XpnsrPageHeader from './XpnsrPageHeader';

const TEMPLATE_TYPES = [
  { value: 'affiliate_network', label: 'Affiliate network postback' },
  { value: 'postback_macro', label: 'Postback macro' },
  { value: 'filter_rule', label: 'Filter rule' },
  { value: 'prelanding', label: 'Pre-landing page' },
];

const TYPE_LABELS = TEMPLATE_TYPES.reduce((acc, t) => {
  acc[t.value] = t.label;
  return acc;
}, {});

const EMPTY_BODIES = {
  affiliate_network: { url_template: '', method: 'GET' },
  postback_macro: { macro: '' },
  filter_rule: { conditions: {} },
  prelanding: { html: '' },
};

const Marketplace = () => {
  const { t } = useT();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [activeTag, setActiveTag] = useState(null);

  const [detailOpen, setDetailOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const [submitOpen, setSubmitOpen] = useState(false);
  const [submitName, setSubmitName] = useState('');
  const [submitType, setSubmitType] = useState('postback_macro');
  const [submitDescription, setSubmitDescription] = useState('');
  const [submitTags, setSubmitTags] = useState('');
  const [submitBodyText, setSubmitBodyText] = useState(
    JSON.stringify(EMPTY_BODIES.postback_macro, null, 2),
  );
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (query.trim()) params.q = query.trim();
      if (typeFilter && typeFilter !== 'all') params.type = typeFilter;
      if (activeTag) params.tag = activeTag;
      const res = await axios.get(`${API}/marketplace/templates`, { params });
      setTemplates(res.data.templates || []);
    } catch (err) {
      toast.error(
        err?.response?.data?.detail ||
          t('Could not load marketplace templates.'),
      );
      setTemplates([]);
    } finally {
      setLoading(false);
    }
  }, [query, typeFilter, activeTag, t]);

  useEffect(() => {
    load();
  }, [load]);

  const allTags = useMemo(() => {
    const set = new Set();
    for (const tt of templates) {
      for (const tag of tt.tags || []) set.add(tag);
    }
    return Array.from(set).sort();
  }, [templates]);

  const openDetail = async (slug) => {
    setDetailOpen(true);
    setDetail(null);
    setDetailLoading(true);
    try {
      const res = await axios.get(`${API}/marketplace/templates/${slug}`);
      setDetail(res.data);
    } catch (err) {
      toast.error(
        err?.response?.data?.detail || t('Could not load template detail.'),
      );
      setDetailOpen(false);
    } finally {
      setDetailLoading(false);
    }
  };

  const install = async (slug) => {
    try {
      const res = await axios.post(`${API}/marketplace/templates/${slug}/install`);
      const body = res.data.body;
      const json =
        typeof body === 'string' ? body : JSON.stringify(body, null, 2);
      try {
        await navigator.clipboard.writeText(json);
        toast.success(t('Template body copied to clipboard.'));
      } catch (_) {
        toast.success(t('Template installed. Body shown in detail dialog.'));
      }
      // Refresh listing so the bumped counter shows up.
      load();
      // Update detail panel if it's open on this slug.
      if (detail && detail.slug === slug) setDetail(res.data);
    } catch (err) {
      toast.error(
        err?.response?.data?.detail || t('Install failed.'),
      );
    }
  };

  const [removeTarget, setRemoveTarget] = useState(null);

  const removeTemplate = async (slug) => {
    try {
      await axios.delete(`${API}/marketplace/templates/${slug}`);
      setRemoveTarget(null);
      toast.success(t('Template removed.'));
      setDetailOpen(false);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('Delete failed.'));
    }
  };

  const onSubmitTypeChange = (value) => {
    setSubmitType(value);
    setSubmitBodyText(JSON.stringify(EMPTY_BODIES[value] || {}, null, 2));
  };

  const submitTemplate = async () => {
    if (!submitName.trim()) {
      toast.error(t('Name is required.'));
      return;
    }
    let parsed;
    try {
      parsed = JSON.parse(submitBodyText);
    } catch (e) {
      toast.error(t('Body must be valid JSON.'));
      return;
    }
    const tags = submitTags
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/marketplace/templates`, {
        name: submitName.trim(),
        type: submitType,
        description: submitDescription.trim(),
        tags,
        body: parsed,
      });
      toast.success(t('Template published as {slug}.').replace('{slug}', res.data.slug));
      setSubmitOpen(false);
      setSubmitName('');
      setSubmitDescription('');
      setSubmitTags('');
      setSubmitBodyText(JSON.stringify(EMPTY_BODIES[submitType] || {}, null, 2));
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('Submission failed.'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="px-8 pt-2 pb-10 max-w-6xl space-y-6">
      <XpnsrPageHeader
        kicker={t('Marketplace')}
        title={t('Template marketplace')}
        subtitle={t(
          'Browse and install community-contributed templates for postbacks, macros, anti-fraud rules, and pre-landings. Submissions auto-publish — the community self-polices via the delete action.',
        )}
        actions={
          <Button onClick={() => setSubmitOpen(true)}>
            <Plus className="w-4 h-4 mr-2" /> {t('Submit template')}
          </Button>
        }
      />

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Search className="w-4 h-4" /> {t('Filters')}
          </CardTitle>
          <CardDescription>
            {t('Search by name, description, or slug. Filter by type or tag.')}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="md:col-span-2">
              <Label htmlFor="market-q">{t('Search')}</Label>
              <Input
                id="market-q"
                value={query}
                placeholder={t('e.g. adcombo, cpa, status')}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') load();
                }}
              />
            </div>
            <div>
              <Label>{t('Type')}</Label>
              <Select value={typeFilter} onValueChange={setTypeFilter}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('Any type')}</SelectItem>
                  {TEMPLATE_TYPES.map((tt) => (
                    <SelectItem key={tt.value} value={tt.value}>
                      {t(tt.label)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          {allTags.length > 0 && (
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-xs text-muted-foreground">
                {t('Tags:')}
              </span>
              {allTags.map((tag) => (
                <button
                  key={tag}
                  type="button"
                  onClick={() => setActiveTag(activeTag === tag ? null : tag)}
                  className={`px-2 py-1 rounded text-xs border ${
                    activeTag === tag
                      ? 'bg-primary text-primary-foreground border-primary'
                      : 'border-border hover:bg-accent'
                  }`}
                >
                  #{tag}
                </button>
              ))}
              {activeTag && (
                <button
                  type="button"
                  onClick={() => setActiveTag(null)}
                  className="text-xs text-muted-foreground underline"
                >
                  {t('clear tag')}
                </button>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {loading ? (
        <div className="text-center text-muted-foreground py-8">
          {t('Loading marketplace…')}
        </div>
      ) : templates.length === 0 ? (
        <Card>
          <CardContent className="text-center text-muted-foreground py-10">
            <Store className="w-8 h-8 mx-auto mb-2 opacity-60" />
            {t('No templates match these filters.')}
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {templates.map((tpl) => (
            <Card key={tpl.slug} className="flex flex-col">
              <CardHeader>
                <div className="flex items-start justify-between gap-2">
                  <CardTitle className="text-base">{tpl.name}</CardTitle>
                  <Badge variant="secondary">
                    {TYPE_LABELS[tpl.type] || tpl.type}
                  </Badge>
                </div>
                <CardDescription className="line-clamp-2">
                  {tpl.description || t('No description provided.')}
                </CardDescription>
              </CardHeader>
              <CardContent className="flex-1 flex flex-col justify-between gap-3">
                <div className="flex flex-wrap gap-1">
                  {(tpl.tags || []).map((tag) => (
                    <Badge key={tag} variant="outline" className="text-xs">
                      #{tag}
                    </Badge>
                  ))}
                </div>
                <div className="flex items-center justify-between gap-2 text-xs text-muted-foreground">
                  <span>
                    {tpl.author_name
                      ? t('By {author}').replace('{author}', tpl.author_name)
                      : t('Anonymous')}
                  </span>
                  <span className="flex items-center gap-1">
                    <Download className="w-3 h-3" /> {tpl.downloads}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => openDetail(tpl.slug)}
                    className="flex-1"
                  >
                    <Eye className="w-4 h-4 mr-1" /> {t('View')}
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => install(tpl.slug)}
                    className="flex-1"
                  >
                    <Download className="w-4 h-4 mr-1" /> {t('Install')}
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Detail dialog */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{detail?.name || t('Template')}</DialogTitle>
            <DialogDescription>
              {detail
                ? `${TYPE_LABELS[detail.type] || detail.type} · ${detail.downloads} ${t('installs')}`
                : t('Loading…')}
            </DialogDescription>
          </DialogHeader>
          {detailLoading ? (
            <div className="text-center text-muted-foreground py-6">
              {t('Loading…')}
            </div>
          ) : detail ? (
            <div className="space-y-4">
              {detail.description && (
                <p className="text-sm text-muted-foreground">
                  {detail.description}
                </p>
              )}
              {detail.tags && detail.tags.length > 0 && (
                <div className="flex flex-wrap gap-1">
                  {detail.tags.map((tag) => (
                    <Badge key={tag} variant="outline" className="text-xs">
                      #{tag}
                    </Badge>
                  ))}
                </div>
              )}
              <div>
                <Label>{t('Body')}</Label>
                <pre className="mt-1 text-xs bg-muted p-3 rounded overflow-x-auto max-h-72">
                  {typeof detail.body === 'string'
                    ? detail.body
                    : JSON.stringify(detail.body, null, 2)}
                </pre>
              </div>
              <div className="flex items-center justify-between text-xs text-muted-foreground">
                <span>
                  {detail.author_name
                    ? t('By {author}').replace('{author}', detail.author_name)
                    : t('Anonymous')}
                </span>
                <span>{detail.created_at?.split('T')[0]}</span>
              </div>
            </div>
          ) : null}
          <DialogFooter className="gap-2 flex-wrap">
            {detail && (
              <>
                <Button
                  variant="outline"
                  onClick={() => {
                    const json =
                      typeof detail.body === 'string'
                        ? detail.body
                        : JSON.stringify(detail.body, null, 2);
                    navigator.clipboard
                      ?.writeText(json)
                      .then(() => toast.success(t('Body copied to clipboard.')))
                      .catch(() => {});
                  }}
                >
                  <Copy className="w-4 h-4 mr-1" /> {t('Copy')}
                </Button>
                <Button onClick={() => install(detail.slug)}>
                  <Download className="w-4 h-4 mr-1" /> {t('Install')}
                </Button>
                <Button
                  variant="destructive"
                  onClick={() => setRemoveTarget(detail)}
                >
                  <Trash2 className="w-4 h-4 mr-1" /> {t('Delete')}
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Submit dialog */}
      <Dialog open={submitOpen} onOpenChange={(open) => { setSubmitOpen(open); if (!open) { setSubmitName(''); setSubmitType('postback_macro'); setSubmitDescription(''); setSubmitTags(''); setSubmitBodyText(JSON.stringify(EMPTY_BODIES.postback_macro, null, 2)); } }}>
        <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t('Submit a template')}</DialogTitle>
            <DialogDescription>
              {t(
                'Submissions auto-publish. The slug is derived from the name; if it collides with an existing template, a numeric suffix is appended.',
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label htmlFor="submit-name">{t('Name')}</Label>
              <Input
                id="submit-name"
                value={submitName}
                onChange={(e) => setSubmitName(e.target.value)}
                placeholder={t('e.g. MaxBounty CPA')}
              />
            </div>
            <div>
              <Label>{t('Type')}</Label>
              <Select value={submitType} onValueChange={onSubmitTypeChange}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TEMPLATE_TYPES.map((tt) => (
                    <SelectItem key={tt.value} value={tt.value}>
                      {t(tt.label)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="submit-description">{t('Description')}</Label>
              <Textarea
                id="submit-description"
                value={submitDescription}
                onChange={(e) => setSubmitDescription(e.target.value)}
                rows={2}
              />
            </div>
            <div>
              <Label htmlFor="submit-tags">
                {t('Tags (comma-separated)')}
              </Label>
              <Input
                id="submit-tags"
                value={submitTags}
                onChange={(e) => setSubmitTags(e.target.value)}
                placeholder={t('cpa, nutra, en')}
              />
            </div>
            <div>
              <Label htmlFor="submit-body">{t('Body (JSON)')}</Label>
              <Textarea
                id="submit-body"
                value={submitBodyText}
                onChange={(e) => setSubmitBodyText(e.target.value)}
                rows={8}
                className="font-mono text-xs"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setSubmitOpen(false)}
              disabled={submitting}
            >
              {t('Cancel')}
            </Button>
            <Button onClick={submitTemplate} disabled={submitting}>
              {submitting ? t('Publishing…') : t('Publish')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!removeTarget}
        onOpenChange={(open) => { if (!open) setRemoveTarget(null); }}
        title={t('Remove template')}
        description={t('Remove this template? This cannot be undone.')}
        confirmLabel={t('Remove')}
        onConfirm={() => removeTemplate(removeTarget?.slug)}
      />
    </div>
  );
};

export default Marketplace;
